"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  FileCheck2,
  Globe2,
  Headphones,
  HeartHandshake,
  Info,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  TriangleAlert,
  UserRoundCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import styles from "./pre-training-review.module.css";
import apiClient from "@/services/apiClient";
import ToastHelper from "@/utils/toastHelper";
import CourseProgressStageLayout from "@/components/course-progress/CourseProgressStageLayout";
import CourseStepValidationDialog from "@/components/course-progress/CourseStepValidationDialog";
import { getCourseProgressUrl } from "@/components/course-progress/courseProgressNavigation";
import { validateCourseProgressStepAccess } from "@/hooks/useStudentCourseProgress";
import { COURSE_STEP_TYPE } from "@/utils/courseStepType";

const steps = [
  { short: "Course", title: "Course and booking", icon: ClipboardCheck },
  { short: "Skills", title: "Experience and skills check", icon: BookOpenCheck },
  { short: "Support", title: "Participation and support", icon: HeartHandshake },
  { short: "Review", title: "Review and declaration", icon: ShieldCheck },
];

const courseQuestions = [
  {
    id: "location",
    number: 1,
    question: "Which GIT location is shown in your booking?",
    help: "Choose the location in your booking confirmation. This answer does not change your booking.",
    options: [
      { value: "pakenham", label: "Pakenham" },
      { value: "north-melbourne", label: "North Melbourne" },
      { value: "not-booked", label: "I have not selected a class yet" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "reason",
    number: 2,
    question: "Why are you completing White Card training?",
    options: [
      { value: "work", label: "For a job or work placement" },
      { value: "study", label: "For study, an apprenticeship or a traineeship" },
      { value: "personal", label: "For personal knowledge or future work" },
      { value: "employer", label: "My employer or another organisation asked me to complete it" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "courseInfo",
    number: 3,
    question: "Have you read the course information, fees, refund rules and learner rights?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "explain", label: "I need part of it explained" },
      { value: "no", label: "Not yet" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "faceToFace",
    number: 4,
    question: "Do you understand that approved Victorian White Card training and assessment is completed face-to-face?",
    help: "This online review helps you prepare. It is not the White Card training or assessment.",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "fullSession",
    number: 5,
    question: "Can you attend the full face-to-face session and arrive 30 minutes before the class starts for check-in?",
    help: "Plan to arrive between 8:30 am and 8:45 am for check-in.",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "photoId",
    number: 6,
    question: "Can you bring current, original photo identification accepted by GIT?",
    help: "The name on your identification must match your enrolment details.",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure which identification is accepted" },
    ],
  },
  {
    id: "usi",
    number: 7,
    question: "What is the status of your Unique Student Identifier (USI)?",
    options: [
      { value: "provided", label: "I have provided my USI to GIT" },
      { value: "find", label: "I have a USI but need to find it" },
      { value: "create", label: "I need to create a USI" },
      { value: "help", label: "I need help with my USI" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
];

const skillsQuestions = [
  {
    id: "experience",
    number: 8,
    question: "Have you worked or studied in construction before?",
    help: "Previous experience is not required.",
    options: [
      { value: "current", label: "Yes, currently" },
      { value: "previous", label: "Yes, in the past" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "priorWhiteCard",
    number: 9,
    question: "Have you held a White Card before?",
    options: [
      { value: "current", label: "Yes, and I still have it" },
      { value: "lost-expired", label: "Yes, but it is lost, damaged or no longer current for my needs" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "signMeaning",
    number: 10,
    question: "A sign says ‘DANGER — DO NOT ENTER’. What should you do?",
    options: [
      { value: "stay-out", label: "Stay outside the area and follow site instructions" },
      { value: "enter-carefully", label: "Enter carefully if the area looks safe" },
      { value: "ignore", label: "Ignore the sign if another worker entered" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "arrivalMath",
    number: 11,
    question: "How many minutes before the class starts should you arrive for check-in?",
    options: [
      { value: "15", label: "15 minutes" },
      { value: "30", label: "30 minutes" },
      { value: "45", label: "45 minutes" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "reading",
    number: 12,
    question: "Can you read simple English safety signs and instructions?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "support", label: "Yes, with some support" },
      { value: "no", label: "Not at the moment" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "communication",
    number: 13,
    question: "Can you listen to instructions and explain a safety hazard in simple English?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "support", label: "Yes, with some support" },
      { value: "no", label: "Not at the moment" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "writing",
    number: 14,
    question: "Can you complete a simple form and write a short safety response in English?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "support", label: "Yes, with some support" },
      { value: "no", label: "Not at the moment" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "digital",
    number: 15,
    question: "Can you use a phone, tablet or computer to open links and complete simple online activities?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "support", label: "Yes, with some help" },
      { value: "no", label: "Not at the moment" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
];

const participationQuestions = [
  {
    id: "assessmentTasks",
    number: 16,
    question: "Can you take part in written questions, spoken questions and practical demonstrations?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "adjustment", label: "Yes, with a reasonable adjustment or support" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "ppe",
    number: 17,
    question: "Can you safely select and fit the required personal protective equipment (PPE)?",
    help: "This includes eye protection, hearing protection, a hard hat and high-visibility clothing. GIT will provide assessment PPE.",
    options: [
      { value: "yes", label: "Yes" },
      { value: "adjustment", label: "Yes, with a reasonable adjustment or support" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "ownWork",
    number: 18,
    question: "Do you understand that you must complete your own assessment work?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "supportContact",
    number: 19,
    question: "Would you like GIT to contact you about learning support or reasonable adjustment?",
    help: "You do not need to provide a diagnosis here. You can ask to discuss your needs privately.",
    options: [
      { value: "no", label: "No support contact is needed" },
      { value: "yes", label: "Yes, please contact me" },
      { value: "private", label: "I would prefer to discuss this privately" },
      { value: "unsure", label: "I am not sure" },
    ],
  },
  {
    id: "preferredContact",
    number: 21,
    question: "If GIT needs to contact you, what method do you prefer?",
    options: [
      { value: "phone", label: "Phone" },
      { value: "email", label: "Email" },
      { value: "in-person", label: "In person" },
      { value: "no-preference", label: "No preference" },
    ],
  },
];

const supportOptions = [
  { value: "none", label: "No support requested" },
  { value: "reading", label: "Reading safety information" },
  { value: "writing", label: "Writing or completing forms" },
  { value: "numbers", label: "Using times or simple numbers" },
  { value: "spoken", label: "Listening or speaking in English" },
  { value: "digital", label: "Using the online portal or a device" },
  { value: "hearing-vision", label: "Hearing or vision access" },
  { value: "mobility", label: "Mobility or venue access" },
  { value: "ppe", label: "Safely fitting PPE" },
  { value: "trainer", label: "Additional trainer explanation or time" },
  { value: "wellbeing", label: "Anxiety, concentration or another learning need" },
  { value: "private", label: "I prefer to discuss my needs privately" },
];

const declarations = [
  {
    id: "accurate",
    label: "I completed this review myself and my answers are true and accurate.",
  },
  {
    id: "notAssessment",
    label: "I understand this review is not White Card training or an assessment of competency.",
  },
  {
    id: "review",
    label: "I understand GIT will review my answers and may contact me about course suitability or support.",
  },
  {
    id: "privacy",
    label: "I consent to GIT using these answers for enrolment, learner support and training administration in line with its privacy obligations.",
  },
];

function buildReviewRequest(studentBookingId, currentStepNo, answers, supportAreas, declarations) {
  return {
    studentBookingId,
    reviewVersion: 1,
    currentStepNo,
    reviewData: { version: 1, answers, supportAreas, declarations },
  };
}

function RadioQuestion({ item, value, invalid, onChange }) {
  return (
    <fieldset className={`${styles.questionCard} ${invalid ? styles.invalidQuestion : ""}`}>
      <legend>
        <span className={styles.questionNumber}>{String(item.number).padStart(2, "0")}</span>
        <span className={styles.questionCopy}>
          <strong>{item.question}</strong>
          {item.help && <small>{item.help}</small>}
        </span>
      </legend>
      <RadioGroup
        value={value}
        onValueChange={onChange}
        className={styles.optionGrid}
        aria-invalid={invalid}
      >
        {item.options.map((option) => {
          const inputId = `${item.id}-${option.value}`;
          return (
            <label className={styles.radioOption} htmlFor={inputId} key={option.value}>
              <RadioGroupItem id={inputId} value={option.value} />
              <span>{option.label}</span>
            </label>
          );
        })}
      </RadioGroup>
      {invalid && <p className={styles.fieldError}>Choose one answer to continue.</p>}
    </fieldset>
  );
}

export default function PreTrainingReviewStage({ mode = "student" }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const studentBookingId = Number(params.studentBookingId || state?.studentBookingId || 0);
  const courseUrl = state?.courseProgressUrl || getCourseProgressUrl(mode, studentBookingId);
  const [currentStep, setCurrentStep] = useState(0);
  const [highestStep, setHighestStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [supportAreas, setSupportAreas] = useState([]);
  const [declarationState, setDeclarationState] = useState({});
  const [missingIds, setMissingIds] = useState([]);
  const [declarationError, setDeclarationError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingReview, setLoadingReview] = useState(studentBookingId > 0);
  const [submittedOutcomeNo, setSubmittedOutcomeNo] = useState(null);
  const [dialog, setDialog] = useState(null);
  const headingRef = useRef(null);

  const progress = ((currentStep + 1) / steps.length) * 100;
  const answeredCount = Object.keys(answers).length + (supportAreas.length > 0 ? 1 : 0);
  const allDeclarationsChecked = declarations.every((item) => declarationState[item.id]);

  const outcome = useMemo(() => {
    const critical = [];
    const support = [];

    const addCritical = (condition, message) => condition && critical.push(message);
    const addSupport = (condition, message) => condition && support.push(message);

    addCritical(answers.faceToFace === "no", "The face-to-face delivery requirement needs to be discussed.");
    addCritical(answers.fullSession === "no", "Full-session attendance cannot currently be confirmed.");
    addCritical(answers.photoId === "no", "Accepted original photo identification is not currently available.");
    addCritical(answers.assessmentTasks === "no", "Participation in required assessment activities cannot currently be confirmed.");
    addCritical(answers.ppe === "no", "Safe participation in the required PPE demonstration cannot currently be confirmed.");
    addCritical(answers.ownWork === "no", "The requirement to complete assessment work independently needs to be discussed.");

    addSupport(["not-booked", "unsure"].includes(answers.location), "Booking location needs confirmation.");
    addSupport(answers.reason === "unsure", "The learner would like help confirming whether this is the right course.");
    addSupport(answers.courseInfo !== "yes", "Course information needs review or explanation.");
    addSupport(answers.faceToFace === "unsure", "Face-to-face delivery needs clarification.");
    addSupport(answers.fullSession === "unsure", "Session attendance needs confirmation.");
    addSupport(answers.photoId === "unsure", "Accepted photo identification needs clarification.");
    addSupport(answers.usi !== "provided", "USI support or follow-up is required.");
    addSupport(["current", "lost-expired", "unsure"].includes(answers.priorWhiteCard), "Previous White Card details should be checked.");
    addSupport(answers.signMeaning !== "stay-out", "The safety sign response should be discussed with the learner.");
    addSupport(answers.arrivalMath !== "30", "The simple time response should be discussed with the learner.");

    [
      ["reading", "Reading support may be helpful."],
      ["communication", "Listening or spoken communication support may be helpful."],
      ["writing", "Writing support may be helpful."],
      ["digital", "Digital support may be helpful."],
    ].forEach(([id, message]) => addSupport(answers[id] !== "yes", message));

    addSupport(["adjustment", "unsure"].includes(answers.assessmentTasks), "Assessment support or reasonable adjustment should be discussed.");
    addSupport(["adjustment", "unsure"].includes(answers.ppe), "PPE support or reasonable adjustment should be discussed.");
    addSupport(answers.ownWork === "unsure", "Assessment authenticity requirements need clarification.");
    addSupport(answers.supportContact !== "no", "The learner requested, or may benefit from, a support conversation.");
    addSupport(supportAreas.some((area) => area !== "none"), "One or more support areas were selected.");

    if (critical.length > 0) {
      return {
        type: "discussion",
        title: "Further suitability discussion needed",
        label: "GIT follow-up required",
        message: "A GIT team member should speak with you before your class or enrolment is confirmed.",
        reasons: [...critical, ...support],
      };
    }

    if (support.length > 0) {
      return {
        type: "support",
        title: "Support review requested",
        label: "GIT will review your answers",
        message: "Your course may still be suitable. GIT should review your answers and contact you if support or clarification is needed.",
        reasons: support,
      };
    }

    return {
      type: "ready",
      title: "Ready for enrolment confirmation",
      label: "Review complete",
      message: "Based on these answers, the course appears suitable. GIT will complete its normal enrolment checks and confirm your class details.",
      reasons: [],
    };
  }, [answers, supportAreas]);

  const displayedOutcome = useMemo(() => {
    if (mode === "admin" && !submittedOutcomeNo) {
      return {
        type: "ready",
        title: "Pre-training review answers",
        label: "Draft review",
        message: "These are the learner's saved answers. The review has not yet been submitted and may be incomplete.",
        reasons: [],
      };
    }
    if (!submittedOutcomeNo) return outcome;
    const type = Number(submittedOutcomeNo) === 3 ? "discussion" : Number(submittedOutcomeNo) === 2 ? "support" : "ready";
    return { ...outcome, type };
  }, [mode, outcome, submittedOutcomeNo]);

  useEffect(() => {
    if (studentBookingId <= 0) {
      setLoadingReview(false);
      return;
    }

    let mounted = true;
    apiClient.get("/CourseProgress/PreTrainingReview", { params: { pStudentBookingId: studentBookingId } })
      .then(({ data }) => {
        if (!mounted) return;
        const reviewData = data?.reviewData ?? {};
        setAnswers(reviewData.answers ?? {});
        setSupportAreas(reviewData.supportAreas ?? []);
        setDeclarationState(reviewData.declarations ?? {});
        const restoredStep = Math.min(3, Math.max(0, Number(data?.currentStepNo || 1) - 1));
        const isSubmitted = Number(data?.reviewStatusNo) === 2;
        setCurrentStep(isSubmitted ? 3 : restoredStep);
        setHighestStep(isSubmitted ? 3 : restoredStep);
        setCompletedSteps(isSubmitted ? [0, 1, 2, 3] : Array.from({ length: restoredStep }, (_, index) => index));
        setSubmittedOutcomeNo(data?.outcomeNo ?? null);
        // The admin route is a read-only record. Show all saved answers without
        // exposing the learner's edit or submit controls.
        setSubmitted(isSubmitted || mode === "admin");
      })
      .catch((error) => ToastHelper.apiError(error, "Unable to load the pre-training review"))
      .finally(() => mounted && setLoadingReview(false));
    return () => { mounted = false; };
  }, [mode, studentBookingId]);

  useEffect(() => {
    if (currentStep > 0) headingRef.current?.focus();
  }, [currentStep]);

  useEffect(() => {
    return () => window.speechSynthesis?.cancel();
  }, []);

  function questionsForStep(index) {
    if (index === 0) return courseQuestions;
    if (index === 1) return skillsQuestions;
    if (index === 2) return participationQuestions;
    return [];
  }

  function updateAnswer(id, value) {
    setAnswers((current) => ({ ...current, [id]: value }));
    setMissingIds((current) => current.filter((item) => item !== id));
  }

  function toggleSupportArea(value, checked) {
    setSupportAreas((current) => {
      if (!checked) return current.filter((item) => item !== value);
      if (value === "none") return ["none"];
      return [...current.filter((item) => item !== "none" && item !== value), value];
    });
    setMissingIds((current) => current.filter((item) => item !== "supportAreas"));
  }

  function validateCurrentStep() {
    const missing = questionsForStep(currentStep)
      .filter((item) => !answers[item.id])
      .map((item) => item.id);

    if (currentStep === 2 && supportAreas.length === 0) missing.push("supportAreas");
    setMissingIds(missing);
    return missing.length === 0;
  }

  function moveToStep(index) {
    if (index > highestStep) return;
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    setMissingIds([]);
    setCurrentStep(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function continueForward() {
    if (!validateCurrentStep()) {
      window.setTimeout(() => {
        document.querySelector(`.${styles.invalidQuestion}`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 0);
      return;
    }

    const nextStep = Math.min(currentStep + 1, steps.length - 1);
    setSaving(true);
    try {
      if (mode === "student") {
        await apiClient.post("/CourseProgress/SavePreTrainingReview", buildReviewRequest(studentBookingId, nextStep + 1, answers, supportAreas, declarationState));
      }
      setCompletedSteps((current) => current.includes(currentStep) ? current : [...current, currentStep]);
      setHighestStep((current) => Math.max(current, nextStep));
      setCurrentStep(nextStep);
      setMissingIds([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save the pre-training review");
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    if (currentStep === 0) {
      navigate(courseUrl, { state: { focusCurrentStep: true } });
      return;
    }
    setMissingIds([]);
    setCurrentStep((current) => Math.max(0, current - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleSpeech() {
    if (!("speechSynthesis" in window)) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const content = document.getElementById("review-content")?.innerText;
    if (!content) return;
    const utterance = new SpeechSynthesisUtterance(content);
    utterance.lang = "en-AU";
    utterance.rate = 0.95;
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
  }

  async function submitReview() {
    if (!allDeclarationsChecked) {
      setDeclarationError(true);
      return;
    }
    setSaving(true);
    try {
      const validation = await validateCourseProgressStepAccess(studentBookingId, COURSE_STEP_TYPE.PRE_TRAINING_REVIEW);
      if (!(validation?.canProceed ?? validation?.CanProceed)) {
        setDialog(validation);
        return;
      }
      const { data } = await apiClient.post("/CourseProgress/CompletePreTrainingReview", buildReviewRequest(studentBookingId, 4, answers, supportAreas, declarationState));
      setDeclarationError(false);
      setCompletedSteps([0, 1, 2, 3]);
      setSubmittedOutcomeNo(data?.outcomeNo ?? null);
      setSubmitted(true);
      ToastHelper.success("Pre-training review completed. LLN is now available.");
      navigate(courseUrl, { state: { focusCurrentStep: true } });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to complete the pre-training review");
    } finally {
      setSaving(false);
    }
  }

  if (loadingReview) {
    return <CourseProgressStageLayout backUrl={courseUrl} className={styles.pageShell} testId="pre-training-review-loading"><p>Loading pre-training review...</p></CourseProgressStageLayout>;
  }

  function answerLabel(question) {
    return question.options.find((option) => option.value === answers[question.id])?.label ?? "Not answered";
  }

  function renderQuestionList(items) {
    return (
      <div className={styles.questionList}>
        {items.map((item) => (
          <RadioQuestion
            key={item.id}
            item={item}
            value={answers[item.id]}
            invalid={missingIds.includes(item.id)}
            onChange={(value) => updateAnswer(item.id, value)}
          />
        ))}
      </div>
    );
  }

  function renderActiveStep() {
    if (currentStep === 0) {
      return (
        <>
          <div className={styles.infoBanner}>
            <Info aria-hidden="true" />
            <div>
              <strong>This is a support and suitability review—not a test.</strong>
              <p>Your answers help GIT confirm that the course is suitable and identify support before training starts. There is no pass or fail result.</p>
            </div>
          </div>
          {renderQuestionList(courseQuestions)}
        </>
      );
    }

    if (currentStep === 1) {
      return (
        <>
          <div className={styles.plainEnglishPanel}>
            <BookOpenCheck aria-hidden="true" />
            <div>
              <strong>Please answer without help from another person.</strong>
              <p>It is okay to choose “I am not sure” or say that support may help. Honest answers let GIT offer the right assistance.</p>
            </div>
          </div>
          {renderQuestionList(skillsQuestions)}
        </>
      );
    }

    if (currentStep === 2) {
      return (
        <>
          {renderQuestionList(participationQuestions.slice(0, 4))}

          <fieldset className={`${styles.questionCard} ${missingIds.includes("supportAreas") ? styles.invalidQuestion : ""}`}>
            <legend>
              <span className={styles.questionNumber}>20</span>
              <span className={styles.questionCopy}>
                <strong>Which areas would you like help with?</strong>
                <small>Select all that apply. You can choose “No support requested”.</small>
              </span>
            </legend>
            <div className={styles.checkboxGrid}>
              {supportOptions.map((option) => {
                const inputId = `support-${option.value}`;
                return (
                  <label className={styles.checkboxOption} htmlFor={inputId} key={option.value}>
                    <Checkbox
                      id={inputId}
                      checked={supportAreas.includes(option.value)}
                      onCheckedChange={(checked) => toggleSupportArea(option.value, checked === true)}
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
            {missingIds.includes("supportAreas") && <p className={styles.fieldError}>Choose at least one option to continue.</p>}
          </fieldset>

          {renderQuestionList(participationQuestions.slice(4))}

          <div className={styles.privacyPanel}>
            <LockKeyhole aria-hidden="true" />
            <div>
              <strong>Your privacy matters</strong>
              <p>Only provide information needed to arrange support. You may ask to discuss personal information privately with GIT.</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={styles.reviewIntro}>
          <FileCheck2 aria-hidden="true" />
          <div>
            <strong>Check your answers before submitting.</strong>
            <p>If an answer is not correct, use the Edit button for that section.</p>
          </div>
          <span>{answeredCount} of 21 answered</span>
        </div>

        {[
          { title: "Course and booking", items: courseQuestions, step: 0 },
          { title: "Experience and skills", items: skillsQuestions, step: 1 },
          { title: "Participation and contact", items: participationQuestions, step: 2 },
        ].map((group) => (
          <section className={styles.reviewSection} key={group.title}>
            <header>
              <h3>{group.title}</h3>
              <Button variant="outline" size="sm" onClick={() => moveToStep(group.step)}>Edit</Button>
            </header>
            <dl>
              {group.items.filter((item) => item.number !== 21).map((item) => (
                <div key={item.id}>
                  <dt>{item.number}. {item.question}</dt>
                  <dd>{answerLabel(item)}</dd>
                </div>
              ))}
              {group.step === 2 && (
                <div>
                  <dt>20. Support areas</dt>
                  <dd>{supportOptions.filter((option) => supportAreas.includes(option.value)).map((option) => option.label).join(", ")}</dd>
                </div>
              )}
              {group.step === 2 && group.items.filter((item) => item.number === 21).map((item) => (
                <div key={item.id}>
                  <dt>{item.number}. {item.question}</dt>
                  <dd>{answerLabel(item)}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        <section className={`${styles.declarationPanel} ${declarationError ? styles.declarationError : ""}`} aria-labelledby="declaration-title">
          <div className={styles.declarationHeading}>
            <ShieldCheck aria-hidden="true" />
            <div>
              <span>Final step</span>
              <h3 id="declaration-title">Declaration and consent</h3>
            </div>
          </div>
          <div className={styles.declarationList}>
            {declarations.map((item) => (
              <label htmlFor={`declaration-${item.id}`} key={item.id}>
                <Checkbox
                  id={`declaration-${item.id}`}
                  checked={declarationState[item.id] === true}
                  onCheckedChange={(checked) => {
                    setDeclarationState((current) => ({ ...current, [item.id]: checked === true }));
                    setDeclarationError(false);
                  }}
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
          {declarationError && <p className={styles.declarationMessage}>Please tick all four declarations before submitting.</p>}
        </section>

      </>
    );
  }

  if (submitted) {
    const OutcomeIcon = displayedOutcome.type === "discussion" ? TriangleAlert : displayedOutcome.type === "support" ? HeartHandshake : CheckCircle2;
    return (
      <CourseProgressStageLayout backUrl={courseUrl} className={styles.pageShell} testId="pre-training-review-result">
        <main className={styles.resultMain} id="review-content">
          <section className={`${styles.resultCard} ${styles[displayedOutcome.type]}`} aria-live="polite">
            {mode === "admin" && (
              <div className={styles.resultNotice}>
                <Info aria-hidden="true" />
                <p><strong>Staff view — read only.</strong> These are the learner's saved pre-training review answers.</p>
              </div>
            )}
            <span className={styles.resultIcon} aria-hidden="true"><OutcomeIcon /></span>
            <span className={styles.resultLabel}>{displayedOutcome.label}</span>
            <h1>{displayedOutcome.title}</h1>
            <p>{displayedOutcome.message}</p>

            {displayedOutcome.reasons.length > 0 && (
              <div className={styles.followUpBox}>
                <strong>Items for GIT to review</strong>
                <ul>
                  {displayedOutcome.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                </ul>
              </div>
            )}

            {[
              { title: "Course and booking", items: courseQuestions },
              { title: "Experience and skills", items: skillsQuestions },
              { title: "Participation and contact", items: participationQuestions },
            ].map((group) => (
              <section className={styles.reviewSection} key={group.title}>
                <header><h3>{group.title}</h3></header>
                <dl>
                  {group.items.filter((item) => item.number !== 21).map((item) => <div key={item.id}><dt>{item.number}. {item.question}</dt><dd>{answerLabel(item)}</dd></div>)}
                  {group.title === "Participation and contact" && <div><dt>20. Support areas</dt><dd>{supportOptions.filter((option) => supportAreas.includes(option.value)).map((option) => option.label).join(", ")}</dd></div>}
                  {group.items.filter((item) => item.number === 21).map((item) => <div key={item.id}><dt>{item.number}. {item.question}</dt><dd>{answerLabel(item)}</dd></div>)}
                </dl>
              </section>
            ))}

            <div className={styles.nextSteps}>
              <h2>What happens next</h2>
              <div>
                <span><Check aria-hidden="true" /></span>
                <p><strong>GIT reviews the information.</strong> Staff may contact you using your preferred contact method.</p>
              </div>
              <div>
                <span><Check aria-hidden="true" /></span>
                <p><strong>Support is discussed before training.</strong> Any reasonable adjustment must keep the unit’s essential skills and safety requirements.</p>
              </div>
              <div>
                <span><Check aria-hidden="true" /></span>
                <p><strong>Your booking is confirmed separately.</strong> Check your email for the final class date, time and location.</p>
              </div>
            </div>

            <div className={styles.resultNotice}>
              <Info aria-hidden="true" />
              <p>This review is not an assessment result and does not issue a White Card or Statement of Attainment.</p>
            </div>

            <div className={styles.resultActions}>
              <Button asChild className={styles.continueButton}>
                <button type="button" onClick={() => navigate(courseUrl, { state: { focusCurrentStep: true } })}>Return to course progress<ArrowRight aria-hidden="true" /></button>
              </Button>
            </div>
          </section>
        </main>
      </CourseProgressStageLayout>
    );
  }

  const ActiveIcon = steps[currentStep].icon;

  return (
    <CourseProgressStageLayout backUrl={courseUrl} className={styles.pageShell} testId="pre-training-review">
      <a className={styles.skipLink} href="#active-review-step">Skip to current review step</a>

      <main className={styles.main} id="review-content">
        <nav className={styles.contextNav} aria-label="Breadcrumb">
          <a href={courseUrl}><ArrowLeft aria-hidden="true" />Course progress</a>
          <ChevronRight aria-hidden="true" />
          <span>White Card</span>
          <ChevronRight aria-hidden="true" />
          <span aria-current="page">Pre-training review</span>
          <div className={styles.accessibilityTools}>
            <span className={styles.languageChip}><Globe2 aria-hidden="true" />English (AU)</span>
            <Button
              variant="outline"
              size="sm"
              className={speaking ? styles.listenActive : styles.listenButton}
              onClick={toggleSpeech}
              aria-pressed={speaking}
            >
              <Headphones aria-hidden="true" />{speaking ? "Stop" : "Listen"}
            </Button>
          </div>
        </nav>

        <section className={styles.courseHeader} aria-labelledby="review-title">
          <div>
            <span className={styles.eyebrow}>CPCWHS1001 · Complete before training</span>
            <h1 id="review-title">White Card pre-training review</h1>
            <p>Short questions to check course suitability, foundation skills and support needs.</p>
          </div>
          <div className={styles.courseMeta}>
            <span><Clock3 aria-hidden="true" />About 5–7 minutes</span>
            <span><UserRoundCheck aria-hidden="true" />No pass or fail</span>
          </div>
        </section>

        <div className={styles.portalWorkspace}>
          <aside className={styles.stepRail} aria-label="Review steps">
            <ol>
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isCurrent = currentStep === index;
                const isComplete = completedSteps.includes(index);
                return (
                  <li key={step.short}>
                    <Button
                      variant="ghost"
                      className={`${styles.stepButton} ${isCurrent ? styles.currentStep : ""} ${isComplete ? styles.completeStep : ""}`}
                      onClick={() => moveToStep(index)}
                      disabled={index > highestStep}
                      aria-current={isCurrent ? "step" : undefined}
                    >
                      <span className={styles.stepMarker} aria-hidden="true">{isComplete ? <Check /> : <Icon />}</span>
                      <span><small>{String(index + 1).padStart(2, "0")}</small><strong>{step.short}</strong></span>
                    </Button>
                  </li>
                );
              })}
            </ol>
            <div className={styles.supportCard}>
              <Phone aria-hidden="true" />
              <span>Need help?</span>
              <a href="tel:+61359415070">03 5941 5070</a>
              <a href="mailto:info@git.vic.edu.au"><Mail aria-hidden="true" />Email GIT</a>
            </div>
          </aside>

          <section className={styles.stepCard} id="active-review-step" aria-labelledby="active-step-title">
            <header className={styles.stepHeader}>
              <span className={styles.activeIcon} aria-hidden="true"><ActiveIcon /></span>
              <div>
                <span>Step {currentStep + 1} of {steps.length}</span>
                <h2 id="active-step-title" ref={headingRef} tabIndex={-1}>{steps[currentStep].title}</h2>
              </div>
              <div className={styles.progressBlock}>
                <span>{Math.round(progress)}% complete</span>
                <Progress value={progress} aria-label={`${Math.round(progress)} percent through the pre-training review`} />
              </div>
            </header>

            <div className={styles.stepBody}>{renderActiveStep()}</div>

            {missingIds.length > 0 && (
              <div className={styles.formError} role="alert">
                <TriangleAlert aria-hidden="true" />
                <p>Please answer every question shown on this step before continuing.</p>
              </div>
            )}

            <footer className={styles.stepActions}>
              <Button variant="outline" onClick={goBack}><ArrowLeft aria-hidden="true" />Back</Button>
              {currentStep < steps.length - 1 ? (
                <Button className={styles.continueButton} onClick={continueForward} disabled={saving}>
                  {saving ? "Saving..." : "Continue"}<ArrowRight aria-hidden="true" />
                </Button>
              ) : (
                <Button className={styles.submitButton} onClick={submitReview} disabled={saving}>
                  <CheckCircle2 aria-hidden="true" />{saving ? "Submitting..." : "Submit review"}
                </Button>
              )}
            </footer>
          </section>
        </div>
      </main>
      {dialog && <CourseStepValidationDialog validation={dialog} onClose={() => setDialog(null)} />}
    </CourseProgressStageLayout>
  );
}
